import { can, type Permission, type TokenResponse, type UserDto } from '@bnn/shared';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, ApiError, applyTokens, clearTokens, getRefreshToken, refreshSession, setAuthLostHandler } from '../lib/api';
import { requestPersistentStorage } from '../lib/idb';
import { getDeviceId, kv } from '../lib/kv';
import { outbox } from '../outbox';

type Status = 'loading' | 'anonymous' | 'authenticated';

interface AuthState {
  status: Status;
  user: UserDto | null;
  /** true when the session was restored from the device without reaching the server */
  offlineSession: boolean;
  login(email: string, password: string): Promise<UserDto>;
  logout(): Promise<void>;
  changePassword(current: string, next: string): Promise<void>;
  can(p: Permission): boolean;
}

const Ctx = createContext<AuthState | null>(null);
const USER_KEY = 'user';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [user, setUser] = useState<UserDto | null>(null);
  const [offlineSession, setOfflineSession] = useState(false);

  const signedIn = useCallback((u: UserDto, offline = false) => {
    setUser(u);
    setOfflineSession(offline);
    setStatus('authenticated');
    outbox.setUser(u.id);
    void kv.set(USER_KEY, u);
    void requestPersistentStorage();
  }, []);

  const signedOut = useCallback(() => {
    setUser(null);
    setOfflineSession(false);
    setStatus('anonymous');
    outbox.setUser(null);
  }, []);

  useEffect(() => {
    setAuthLostHandler(signedOut);
    (async () => {
      try {
        const u = await refreshSession();
        if (u) signedIn(u);
        else signedOut();
      } catch (e) {
        // No network: keep working with the session stored on this device. The refresh token is
        // still there, so the server session resumes as soon as the device is back online.
        const cached = await kv.get<UserDto>(USER_KEY);
        if (e instanceof ApiError && e.code === 'NETWORK' && cached && (await getRefreshToken())) signedIn(cached, true);
        else signedOut();
      }
    })();
  }, [signedIn, signedOut]);

  // Back online after an offline start: refresh the session in the background.
  useEffect(() => {
    if (!offlineSession) return;
    const retry = () =>
      void refreshSession()
        .then((u) => (u ? signedIn(u) : signedOut()))
        .catch(() => undefined);
    window.addEventListener('online', retry);
    return () => window.removeEventListener('online', retry);
  }, [offlineSession, signedIn, signedOut]);

  const login = useCallback(
    async (email: string, password: string) => {
      const t = await api.post<TokenResponse>('/auth/login', { email, password, device_id: await getDeviceId() });
      const u = await applyTokens(t);
      signedIn(u);
      return u;
    },
    [signedIn],
  );

  const logout = useCallback(async () => {
    const rt = await getRefreshToken();
    if (rt) await api.post('/auth/logout', { refresh_token: rt }).catch(() => undefined);
    await clearTokens();
    await kv.del(USER_KEY);
    signedOut();
  }, [signedOut]);

  const changePassword = useCallback(
    async (current: string, next: string) => {
      const t = await api.post<TokenResponse>('/auth/change-password', {
        current_password: current,
        new_password: next,
        device_id: await getDeviceId(),
      });
      signedIn(await applyTokens(t));
    },
    [signedIn],
  );

  const value = useMemo<AuthState>(
    () => ({ status, user, offlineSession, login, logout, changePassword, can: (p) => can(user?.role, p) }),
    [status, user, offlineSession, login, logout, changePassword],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
